import {
  getObject,
  getList,
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { KeycloakToken, KeycloakTokenDTO } from "./keycloakToken";
import { UserName, UserNameDTO } from "../types/userName";
/* eslint-disable import/export */
export abstract class SignInRPC implements
  LeoRPC<SignInRPC.Request, SignInRPC.Response, SignInRPC.Errors.Errors>
{
  abstract execute(
    request: SignInRPC.Request
  ): Promise<LeoRPCResult<SignInRPC.Response, SignInRPC.Errors.Errors>>;
}

export namespace SignInRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    token: KeycloakTokenDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isSignInRPCRequest: boolean = true;
  
    readonly token: KeycloakToken;
  
  
    constructor(
    token: KeycloakToken
    ) {
      this.token = token;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const token = KeycloakToken.fromDTO(getObject(dto, "token", InvalidRequestTokenError));
      return new Request(
        token
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          token: this.token.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `SignInRPC(token='${this.token}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    privileges: string[];
    userName: UserNameDTO;
    userId: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isSignInResponse: boolean = true;
  
    readonly privileges: string[];
    readonly userName: UserName;
    readonly userId: LeoUUID;
  
    static readonly constraints = {
      "privileges": {
        "minSize": 1,
      },
    }
  
    constructor(
    privileges: string[], 
    userName: UserName, 
    userId: LeoUUID
    ) {
      Response.validatePrivileges(privileges);
      this.privileges = privileges;
      this.userName = userName;
      this.userId = userId;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const privileges = getList<string, string>(
        dto,
        "privileges",
        (e: string) => e,
        InvalidResponsePrivilegesError
      );
      const userName = UserName.fromDTO(getObject(dto, "userName", InvalidResponseUserNameError));
      const userId = getLeoUuid(dto, "userId", InvalidResponseUserIdError);
      return new Response(
        privileges,
        userName,
        userId
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          privileges: this.privileges,
          userName: this.userName.toDTO(),
          userId: this.userId.toDTO()
      };
      return dto;
    }
    private static validatePrivileges = (privileges: string[]): void => {
      if(privileges.length < 1) {
        throw new InvalidResponsePrivilegesMinSizeError(
          1,
          `Attribute privileges is too short. Size is ${privileges.length}. Min size is 1.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `SignIn(privileges='${this.privileges}',userName='${this.userName}',userId='${this.userId}')`;
  };
  

  export enum RPCError {
    InvalidAccessToken = "INVALID_ACCESS_TOKEN"
  }

  export namespace Errors {
    
    export interface InvalidAccessTokenDTO {
      code: string;
    }
    
    export class InvalidAccessToken extends LeoRPCError<RPCError> {
      readonly isInvalidAccessToken: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidAccessToken;
        super(code);
      }
    
    
      toDTO(): InvalidAccessTokenDTO {
        const dto: InvalidAccessTokenDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidAccessToken.prototype.toString = function toString(): string {
      return `SignInRPC.INVALID_ACCESS_TOKEN()`;
    };
    

    export type Errors = InvalidAccessToken;
    export type ErrorsDTO = InvalidAccessTokenDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidAccessToken:
          return new InvalidAccessToken();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestTokenError extends SignInRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestTokenError";
  }
}

export class InvalidResponsePrivilegesError extends SignInRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePrivilegesError";
  }
}

export class InvalidResponsePrivilegesMinSizeError extends InvalidResponsePrivilegesError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePrivilegesMinSizeError";
  }
}

export class InvalidResponseUserNameError extends SignInRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUserNameError";
  }
}

export class InvalidResponseUserIdError extends SignInRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUserIdError";
  }
}

export class InvalidResponseInvalidAccessTokenError extends SignInRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidAccessTokenError";
  }
}

